import type { FieldDefinition } from '../data/model';
import { attributeRuleField } from './fields';
import type {
  AttributeRule,
  AttributeRuleGroup,
  AttributeRuleGroupOperator,
  AttributeRuleOperator,
} from './model';
import { attributeRuleOperatorDefinition } from './operators';

export const MAX_ATTRIBUTE_RULE_DEPTH = 4;
export const MAX_ATTRIBUTE_RULE_CHILDREN = 20;
export const MAX_ATTRIBUTE_RULE_NODES = 100;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactlyKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const actualKeys = Object.keys(value);
  return actualKeys.length === keys.length && actualKeys.every((key) => keys.includes(key));
}

function isGroupOperator(value: unknown): value is AttributeRuleGroupOperator {
  return value === 'and' || value === 'or';
}

function isConditionValid(
  value: Record<string, unknown>,
  fields: readonly FieldDefinition[],
): boolean {
  if (
    !hasExactlyKeys(value, ['kind', 'field', 'operator', 'value']) ||
    value.kind !== 'condition' ||
    typeof value.field !== 'string' ||
    typeof value.operator !== 'string'
  ) {
    return false;
  }

  const field = attributeRuleField(fields, value.field);
  const definition = field
    ? attributeRuleOperatorDefinition(field, value.operator as AttributeRuleOperator)
    : null;
  if (!definition) return false;

  switch (definition.valueKind) {
    case 'none':
      return value.value === null;
    case 'number':
      return typeof value.value === 'number' && Number.isFinite(value.value);
    case 'string':
      return typeof value.value === 'string';
  }
}

interface ValidationState {
  nodeCount: number;
}

function isNodeValid(
  value: unknown,
  fields: readonly FieldDefinition[],
  depth: number,
  state: ValidationState,
): value is AttributeRule {
  state.nodeCount += 1;
  if (state.nodeCount > MAX_ATTRIBUTE_RULE_NODES || depth > MAX_ATTRIBUTE_RULE_DEPTH) {
    return false;
  }
  if (!isRecord(value)) return false;
  if (value.kind === 'condition') return isConditionValid(value, fields);
  if (
    !hasExactlyKeys(value, ['kind', 'operator', 'children']) ||
    value.kind !== 'group' ||
    !isGroupOperator(value.operator) ||
    !Array.isArray(value.children) ||
    value.children.length === 0 ||
    value.children.length > MAX_ATTRIBUTE_RULE_CHILDREN
  ) {
    return false;
  }
  return value.children.every((child) => isNodeValid(child, fields, depth + 1, state));
}

export function isAttributeRuleValid(
  value: unknown,
  fields: readonly FieldDefinition[],
): value is AttributeRule {
  return isNodeValid(value, fields, 0, { nodeCount: 0 });
}

export function isAttributeRuleGroupValid(
  value: unknown,
  fields: readonly FieldDefinition[],
): value is AttributeRuleGroup {
  return isAttributeRuleValid(value, fields) && value.kind === 'group';
}
